import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, HelpCircle, LogOut, ChevronDown, Moon, Sun, Shield } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../state/auth';
import { useTheme } from '../../lib/theme';
import { useTranslation } from '../../i18n';

interface ProfileMenuProps {
  userName?: string;
  userRole?: string;
  userAvatar?: string;
  isDarkBg?: boolean;
}

const SUPERVISOR_DISPLAY_EMAILS = new Set([
  'marvin.pickett@emoryhealthcare.org',
  'russell.johnson@emoryhealthcare.org',
  'vickie.green@emoryhealthcare.org',
]);

const formatRole = (role: string, hospitalIds?: string[] | null, email?: string | null): string => {
  switch (role) {
    case 'ADMIN': return 'Manager';
    case 'DIRECTOR': return 'Director';
    case 'SUPERVISOR':
      if (email && SUPERVISOR_DISPLAY_EMAILS.has(email.toLowerCase())) return 'Supervisor';
      if (hospitalIds && hospitalIds.length > 0) return 'Director';
      return 'Supervisor';
    case 'GUARD': return 'Security Guard';
    default: return role;
  }
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  userName,
  userRole,
  userAvatar,
  isDarkBg = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const isDarkMode = theme === 'dark';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const resolvedName = userName || authUser?.name || 'Account';
  const resolvedRole = userRole || authUser?.role || 'GUARD';
  const formattedRole = formatRole(resolvedRole, authUser?.hospital_ids, authUser?.email);
  const settingsPath = resolvedRole === 'GUARD' ? '/guard/settings' : '/supervisor/settings';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSettings = () => {
    navigate(settingsPath);
    setIsOpen(false);
  };

  const handleSupport = () => {
    window.location.href = 'mailto:support@g-datalabs.com';
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
          isDarkBg
            ? (isOpen ? "bg-white/10" : "hover:bg-white/10")
            : (isOpen ? "bg-gray-100" : "hover:bg-gray-100")
        )}
        aria-label="Open profile menu"
      >
        {/* Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-medium brand-blue-gradient"
        >
          {userAvatar ? (
            <img src={userAvatar} alt={resolvedName} className="w-full h-full rounded-full" />
          ) : (
            resolvedName.charAt(0).toUpperCase()
          )}
        </div>

        {/* Name and Role */}
        <div className="text-left hidden sm:block">
          <p className={cn("text-sm font-medium", isDarkBg ? "text-white" : "text-gray-900")}>{resolvedName}</p>
          <p className={cn("text-xs", isDarkBg ? "text-[#BFE18E]" : "text-gray-500")}>{formattedRole}</p>
        </div>

        <ChevronDown className={cn(
          "w-4 h-4 transition-transform",
          isDarkBg ? "text-[#BFE18E]" : "text-gray-400",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg animate-fade-in">
            {/* User Info */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-medium brand-blue-gradient"
                >
                  {userAvatar ? (
                    <img src={userAvatar} alt={resolvedName} className="w-full h-full rounded-full" />
                  ) : (
                    resolvedName.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{resolvedName}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    {formattedRole}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button
                onClick={handleSettings}
                className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors text-left"
              >
                <Settings className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-800">{t('profile.settings')}</span>
              </button>
              <a
                href="mailto:support@g-datalabs.com"
                className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <HelpCircle className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-800">{t('profile.help')}</span>
              </a>
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isDarkMode ? (
                    <Moon className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Sun className="w-4 h-4 text-gray-600" />
                  )}
                  <span className="text-sm text-gray-800">{t('profile.darkMode')}</span>
                </div>
                <div className={cn(
                  "w-10 h-5 rounded-full transition-colors relative",
                  isDarkMode ? "bg-blue-500" : "bg-gray-300"
                )}>
                  <div className={cn(
                    "absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform",
                    isDarkMode ? "translate-x-5" : "translate-x-0.5"
                  )} />
                </div>
              </button>
            </div>

            {/* Logout */}
            <div className="border-t border-gray-200 py-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-red-600 transition-colors text-left"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t('auth.signOut')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
